/**
 * Job Stages Pipeline Component
 *
 * Horizontal stepper showing job processing stages with visual indicators
 * for completed, in-progress, and failed states.
 */
import {
  Clock,
  Upload,
  Sparkles,
  Video,
  Cloud,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { JobStatus } from "@/lib/instantdb";

type StageStatus = "completed" | "in_progress" | "pending" | "failed";

interface Stage {
  id: string;
  label: string;
  icon: React.ElementType;
}

const STAGES: Stage[] = [
  { id: "queued", label: "Queued", icon: Clock },
  { id: "uploading", label: "Uploading", icon: Upload },
  { id: "analyzing", label: "Analyzing", icon: Sparkles },
  { id: "rendering", label: "Rendering", icon: Video },
  { id: "transcoding", label: "Transcoding", icon: Cloud },
  { id: "completed", label: "Completed", icon: CheckCircle },
];

// Maps job status to current stage index
function getStageIndex(status: JobStatus): number {
  switch (status) {
    case "queued":
      return 0;
    case "uploading":
      return 1;
    case "analytics":
    case "analyzing":
      return 2;
    case "rendering":
      return 3;
    case "transcoding":
      return 4;
    case "completed":
      return 5;
    case "failed":
      // Find which stage failed based on where it stopped
      return -1; // Will be handled separately
    default:
      return 0;
  }
}

// Determines stage status based on job status and stage position
function getStageStatus(
  stageIndex: number,
  jobStatus: JobStatus,
  failedAtStage?: number
): StageStatus {
  const currentStageIndex = getStageIndex(jobStatus);

  if (jobStatus === "failed") {
    // Failed jobs: stages before failure are completed, failed stage is failed, rest are pending
    if (failedAtStage !== undefined) {
      if (stageIndex < failedAtStage) return "completed";
      if (stageIndex === failedAtStage) return "failed";
      return "pending";
    }
    // Default: assume failed at last known stage (uploading -> analyzing -> rendering)
    return stageIndex === 0 ? "completed" : stageIndex <= 3 ? "failed" : "pending";
  }

  if (stageIndex < currentStageIndex) return "completed";
  if (stageIndex === currentStageIndex) return "in_progress";
  return "pending";
}

// Infer which stage a job failed at based on progress
function inferFailedStage(progress: number): number {
  // Use progress to infer which stage the job was in when it failed
  // 0-5%: queued/uploading, 5-50%: analyzing, 50-90%: rendering/transcoding, 90-100%: finalizing
  if (progress >= 55) return 4; // Failed during transcoding (async render)
  if (progress >= 50) return 3; // Failed during rendering
  if (progress >= 5) return 2; // Failed during analyzing
  if (progress > 0) return 1; // Failed during uploading
  return 0; // Failed while queued (rare)
}

interface JobStagesPipelineProps {
  status: JobStatus;
  progress: number;
  message?: string;
}

export function JobStagesPipeline({
  status,
  progress,
  message,
}: JobStagesPipelineProps) {
  const currentStageIndex = getStageIndex(status);
  const failedAtStage =
    status === "failed" ? inferFailedStage(progress) : undefined;

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Processing Pipeline
        </h3>
        <div className="flex items-center justify-between px-2">
          {STAGES.map((stage, index) => {
            const stageStatus = getStageStatus(index, status, failedAtStage);
            const isCurrentStage =
              index === currentStageIndex ||
              (status === "failed" && index === failedAtStage);

            return (
              <div key={stage.id} className="flex flex-1 items-center">
                <StageCircle
                  stage={stage}
                  stageStatus={stageStatus}
                  progress={isCurrentStage ? progress : undefined}
                  message={isCurrentStage ? message : undefined}
                />
                {index < STAGES.length - 1 && (
                  <ConnectorLine
                    completed={getStageStatus(index, status, failedAtStage) === "completed"}
                  />
                )}
              </div>
            );
          })}
        </div>
        {/* Current stage label with progress */}
        <div className="text-center">
          {status === "completed" ? (
            <span className="text-sm font-medium text-emerald-600">
              Completed
            </span>
          ) : status === "failed" ? (
            <span className="text-sm font-medium text-red-600">
              Failed at {STAGES[failedAtStage ?? 0].label}
            </span>
          ) : (
            <div className="space-y-1">
              <span className="text-sm font-medium text-violet-600">
                {STAGES[currentStageIndex].label} ({progress}%)
              </span>
              {message && (
                <p className="text-xs text-muted-foreground">{message}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

// ========== Sub-components ==========

interface StageCircleProps {
  stage: Stage;
  stageStatus: StageStatus;
  progress?: number;
  message?: string;
}

function StageCircle({ stage, stageStatus, progress, message }: StageCircleProps) {
  const Icon = stage.icon;

  const circleStyles: Record<StageStatus, string> = {
    completed: "border-emerald-500 bg-emerald-50 text-emerald-600",
    in_progress: "border-violet-500 bg-violet-50 text-violet-600",
    pending: "border-muted bg-muted/50 text-muted-foreground",
    failed: "border-red-500 bg-red-50 text-red-600",
  };

  const renderIcon = () => {
    if (stageStatus === "completed") {
      return <CheckCircle className="h-4 w-4" />;
    }
    if (stageStatus === "failed") {
      return <XCircle className="h-4 w-4" />;
    }
    if (stageStatus === "in_progress") {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    return <Icon className="h-4 w-4" />;
  };

  const tooltipContent = () => {
    switch (stageStatus) {
      case "completed":
        return `${stage.label} - Complete`;
      case "in_progress":
        return `${stage.label} - ${progress ?? 0}%${message ? `\n${message}` : ""}`;
      case "failed":
        return `${stage.label} - Failed`;
      default:
        return stage.label;
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors",
            circleStyles[stageStatus]
          )}
        >
          {renderIcon()}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="whitespace-pre-line">{tooltipContent()}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface ConnectorLineProps {
  completed: boolean;
}

function ConnectorLine({ completed }: ConnectorLineProps) {
  return (
    <div
      className={cn(
        "mx-2 h-0.5 flex-1 transition-colors",
        completed ? "bg-emerald-500" : "bg-muted"
      )}
    />
  );
}
